import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Field,
  Input,
  Link,
  makeStyles,
  Radio,
  RadioGroup,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import { ArrowLeft16Regular } from '@fluentui/react-icons';
import { Question, ResolvedAgreementType } from '../../services/agreementTypes';
import { clearSavedAnswers, getSavedAnswers, saveAnswers } from '../../services/agreementAnswers';

/* global HTMLInputElement, HTMLTextAreaElement */

interface AgreementQuestionsProps {
  agreementType: ResolvedAgreementType;
  onBack: () => void;
  onSubmit: (answers: Record<string, string>) => void;
  disabled?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px',
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  clearLink: {
    fontSize: tokens.fontSizeBase200,
    marginLeft: 'auto',
  },
});

function isQuestionAnswered(q: Question, value: string | undefined): boolean {
  if (q.required === false) return true;
  return !!value && value.trim().length > 0;
}

const AgreementQuestions: React.FC<AgreementQuestionsProps> = ({
  agreementType,
  onBack,
  onSubmit,
  disabled,
}) => {
  const styles = useStyles();
  // Initialize from localStorage so the user resumes where they left off.
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    getSavedAnswers(agreementType.id),
  );

  // Persist on every change so refreshing the pane / switching tabs doesn't
  // lose progress. Cheap (a single localStorage write).
  useEffect(() => {
    saveAnswers(agreementType.id, answers);
  }, [agreementType.id, answers]);

  const setAnswer = (id: string, value: string) => setAnswers(prev => ({ ...prev, [id]: value }));

  const allRequiredFilled = useMemo(
    () => agreementType.questions.every(q => isQuestionAnswered(q, answers[q.id])),
    [agreementType.questions, answers],
  );

  const handleClear = useCallback(() => {
    setAnswers({});
    clearSavedAnswers(agreementType.id);
  }, [agreementType.id]);

  const hasAnyAnswer = Object.values(answers).some(v => v && v.trim().length > 0);

  return (
    <div className={styles.root}>
      <div className={styles.headerRow}>
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowLeft16Regular />}
          onClick={onBack}
          disabled={disabled}
        >
          Back
        </Button>
        <span className={styles.title}>{agreementType.fullName}</span>
      </div>

      {agreementType.questions.map(q => {
        const value = answers[q.id] ?? '';
        const required = q.required !== false;
        const labelText = required ? q.prompt : `${q.prompt} (optional)`;

        if (q.type === 'select' && q.options) {
          return (
            <Field key={q.id} label={labelText}>
              <RadioGroup
                value={value}
                onChange={(_, data) => setAnswer(q.id, data.value)}
                disabled={disabled}
              >
                {q.options.map(opt => (
                  <Radio key={opt} value={opt} label={opt} />
                ))}
              </RadioGroup>
            </Field>
          );
        }

        if (q.type === 'longtext') {
          return (
            <Field key={q.id} label={labelText}>
              <Textarea
                value={value}
                placeholder={q.placeholder}
                rows={3}
                disabled={disabled}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setAnswer(q.id, e.target.value)
                }
              />
            </Field>
          );
        }

        return (
          <Field key={q.id} label={labelText}>
            <Input
              value={value}
              placeholder={q.placeholder}
              disabled={disabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAnswer(q.id, e.target.value)}
            />
          </Field>
        );
      })}

      <div className={styles.actions}>
        <Button
          appearance="primary"
          disabled={disabled || !allRequiredFilled}
          onClick={() => onSubmit(answers)}
        >
          Generate agreement
        </Button>
        {hasAnyAnswer && (
          <Link
            className={styles.clearLink}
            onClick={() => {
              if (!disabled) handleClear();
            }}
          >
            Clear
          </Link>
        )}
      </div>
    </div>
  );
};

export default AgreementQuestions;
